import axios from 'axios';
import {
  AiUsage,
  ClientAiAnalysis,
  SaveAnalysisRequest,
  DocumentPreview,
  AiAnalysisMetadata,
  AiAnalysisJsonResponse
} from '../types/aiAnalysis';
import { getAuthToken } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

/**
 * Helper function to process a single SSE line
 */
const processLine = (
  line: string,
  onChunk: (text: string) => void,
  onMetadata: (metadata: AiAnalysisMetadata) => void,
  onError: (error: string) => void,
  onDone: () => void
): boolean => {
  if (!line.startsWith('data: ')) return false;

  const rawData = line.slice(6);
  // Step 1: Unescape SSE newlines back to actual newlines
  let data = rawData.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
  // Step 2: Restore JSON escape sequences from placeholders
  data = data
    .replace(/__ESCAPED_N__/g, '\\n')
    .replace(/__ESCAPED_R__/g, '\\r')
    .replace(/__ESCAPED_T__/g, '\\t');

  if (data === '[DONE]') {
    onDone();
    return true; // Signal to stop processing
  }

  if (data.startsWith('[ERROR]')) {
    onError(data.replace('[ERROR] ', ''));
    return true; // Signal to stop processing
  }

  if (data.startsWith('[METADATA]') && data.endsWith('[/METADATA]')) {
    const metadataJson = data.replace('[METADATA]', '').replace('[/METADATA]', '');
    try {
      const metadata = JSON.parse(metadataJson) as AiAnalysisMetadata;
      onMetadata(metadata);
    } catch (e) {
      console.error('Failed to parse metadata:', e);
    }
    return false;
  }

  onChunk(data);
  return false;
};

/**
 * AI Analysis API Service
 * שירות API לניתוח AI
 */
export const aiAnalysisApi = {
  /**
   * Get current AI API usage statistics
   * קבלת סטטיסטיקות שימוש ב-AI
   */
  getUsage: async (): Promise<AiUsage> => {
    const response = await axios.get(`${API_BASE_URL}/aianalysis/usage`);
    return response.data;
  },

  /**
   * Get the latest saved analysis for a client
   * קבלת הניתוח השמור האחרון עבור לקוח
   */
  getLatestAnalysis: async (clientId: number): Promise<ClientAiAnalysis | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/aianalysis/client/${clientId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get document previews for a client
   * קבלת תצוגה מקדימה של מסמכים
   */
  getDocumentPreviews: async (clientId: number): Promise<DocumentPreview[]> => {
    const response = await axios.get(`${API_BASE_URL}/aianalysis/documents/${clientId}`);
    return response.data;
  },

  /**
   * Stream analysis generation from AI using Server-Sent Events
   * הזרמת ניתוח מ-AI באמצעות SSE
   */
  streamAnalysis: (
    clientId: number,
    onChunk: (text: string) => void,
    onMetadata: (metadata: AiAnalysisMetadata) => void,
    onError: (error: string) => void,
    onDone: () => void
  ): AbortController => {
    const controller = new AbortController();
    const token = getAuthToken();

    const fetchStream = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/aianalysis/generate/${clientId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          signal: controller.signal
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `שגיאה ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = ''; // Buffer for incomplete lines

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // Process any remaining buffer content
            if (buffer.trim()) {
              processLine(buffer, onChunk, onMetadata, onError, onDone);
            }
            onDone();
            break;
          }

          // Append new data to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete lines only
          const lines = buffer.split('\n');

          // Keep the last (potentially incomplete) line in the buffer
          buffer = lines.pop() || '';

          // Process all complete lines
          for (const line of lines) {
            const shouldStop = processLine(line, onChunk, onMetadata, onError, onDone);
            if (shouldStop) return;
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          // Stream was cancelled, don't report as error
          return;
        }
        onError(error.message || 'שגיאה בחיבור לשרת');
      }
    };

    fetchStream();
    return controller;
  },

  /**
   * Save a generated analysis
   * שמירת ניתוח שנוצר
   */
  saveAnalysis: async (request: SaveAnalysisRequest): Promise<ClientAiAnalysis> => {
    const response = await axios.post(`${API_BASE_URL}/aianalysis/save`, request);
    return response.data;
  }
};

/**
 * Parse JSON response from AI
 * חילוץ תשובת JSON מה-AI
 */
export const parseAiJsonResponse = (content: string): AiAnalysisJsonResponse | null => {
  try {
    // Try to extract JSON from the content
    let jsonStr = content.trim();

    // Remove any markdown code block markers if present
    if (jsonStr.startsWith('```json')) {
      jsonStr = jsonStr.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (jsonStr.startsWith('```')) {
      jsonStr = jsonStr.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    // Try to find JSON object boundaries
    const startIndex = jsonStr.indexOf('{');
    const endIndex = jsonStr.lastIndexOf('}');

    if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
      jsonStr = jsonStr.substring(startIndex, endIndex + 1);
    }

    // SIMPLE FIX: Remove ALL control characters (newlines, tabs, carriage returns, etc.)
    // This makes the JSON a single line - which is perfectly valid JSON
    // The UI will handle formatting for display
    // eslint-disable-next-line no-control-regex
    jsonStr = jsonStr.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');

    const parsed = JSON.parse(jsonStr) as AiAnalysisJsonResponse;

    // Validate required fields
    if (!parsed.medicalSummary && !parsed.diagnoses && !parsed.estimatedDisabilityPercentage) {
      console.warn('Parsed JSON missing required fields');
      return null;
    }

    return parsed;
  } catch (e) {
    console.error('Failed to parse AI JSON response:', e);
    return null;
  }
};

/**
 * Error handler for AI Analysis API
 * טיפול בשגיאות של API ניתוח AI
 */
export const handleAiAnalysisError = (error: any): string => {
  if (error.response) {
    const data = error.response.data;
    // Handle ASP.NET Core validation error format {type, title, status, errors, traceId}
    if (typeof data === 'object' && data !== null) {
      return data.title || data.message || 'שגיאה בשירות AI';
    }
    return String(data) || 'שגיאה בשירות AI';
  } else if (error.request) {
    return 'לא ניתן להתחבר לשרת. אנא בדוק את החיבור לאינטרנט';
  } else {
    return error.message || 'שגיאה לא צפויה';
  }
};

export default aiAnalysisApi;
